import { useEffect, useState, useCallback } from "react";
import { C, S } from "../customers/theme";
import { getProductCategories, createCategory, updateCategory, deleteCategory } from "../lib/products";

/**
 * Modal to manage product categories (list / add / rename / delete).
 * Calls onChanged() after any mutation so the parent can refresh its list/filter.
 * All DB access goes through the products data layer.
 */
export default function CategoryManager({ onClose, onChanged }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // { id, name }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await getProductCategories(); setCats(data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const notify = () => onChanged && onChanged();

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true); setError("");
    try { await createCategory({ name: newName.trim() }); setNewName(""); await load(); notify(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const saveEdit = async () => {
    if (!editing?.name.trim()) return;
    setBusy(true); setError("");
    try { await updateCategory(editing.id, { name: editing.name.trim() }); setEditing(null); await load(); notify(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const remove = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?\nProducts in it will become uncategorised.`)) return;
    setBusy(true); setError("");
    try { await deleteCategory(cat.id); await load(); notify(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 520 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Manage Categories</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input style={{ ...S.input, flex: 1 }} placeholder="New category name" value={newName}
            onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} aria-label="New category name" />
          <button style={S.btn()} onClick={add} disabled={busy || !newName.trim()}>Add</button>
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginBottom: 12 }} role="alert">{error}</div>}

        {loading ? (
          <div style={S.empty}>Loading…</div>
        ) : cats.length === 0 ? (
          <div style={S.empty}>No categories yet. Add one above.</div>
        ) : (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            {cats.map((cat, i) => (
              <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderTop: i ? `1px solid ${C.borderSoft}` : "none", background: C.surface2 }}>
                {editing?.id === cat.id ? (
                  <>
                    <input style={{ ...S.input, flex: 1 }} value={editing.name} autoFocus
                      onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && saveEdit()} aria-label="Category name" />
                    <button style={S.btn()} onClick={saveEdit} disabled={busy}>Save</button>
                    <button style={S.btn("ghost")} onClick={() => setEditing(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 14 }}>{cat.name}</span>
                    <button style={S.btn("ghost")} title="Rename" aria-label={`Rename ${cat.name}`} onClick={() => setEditing({ id: cat.id, name: cat.name })}>✎</button>
                    <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${cat.name}`} onClick={() => remove(cat)}>🗑</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button style={S.btn("secondary")} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
